import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import type { Comment } from '@/types/comment'
import { useAppStore } from '@/store'
import CommentBubble from './CommentBubble'
import CommentInput from './CommentInput'
import { colors } from '@/styles/colors'
import { useTranslation } from '@/i18n'

interface CommentThreadProps {
  workspaceId: string
  showResolvedComments?: boolean
}

const CommentThread = ({
  workspaceId,
  showResolvedComments = true,
}: CommentThreadProps) => {
  const { t } = useTranslation()
  const {
    user,
    comments,
    isLoadingComments,
    loadComments,
    replyingTo,
    setReplyingTo,
    setEditingComment,
  } = useAppStore()

  const [, setLocalEditContent] = useState('')

  useEffect(() => {
    if (workspaceId) {
      loadComments(workspaceId, {
        parent_comment_id: null, // Load only top-level comments
        include_resolved: showResolvedComments,
      })
    }
  }, [workspaceId, loadComments, showResolvedComments])

  const workspaceComments = comments[workspaceId] || []

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId)
  }

  const handleEdit = (commentId: string) => {
    const comment = findCommentById(commentId, workspaceComments)
    if (comment) {
      setEditingComment(commentId)
      setLocalEditContent(comment.content)
    }
  }

  const handleCancelReply = () => {
    setReplyingTo(null)
  }

  const handleCommentCreated = () => {
    loadComments(workspaceId, {
      parent_comment_id: null,
      include_resolved: showResolvedComments,
    })
  }

  const findCommentById = (
    id: string,
    commentList: Comment[]
  ): Comment | null => {
    for (const comment of commentList) {
      if (comment.id === id) return comment
      if (comment.replies.length > 0) {
        const found = findCommentById(id, comment.replies)
        if (found) return found
      }
    }
    return null
  }

  const renderComment = (comment: Comment, depth: number = 0) => {
    const isOwnComment = comment.user_id === user?.userId
    const isReplying = replyingTo === comment.id
    const hasReplies = comment.replies.length > 0
    const maxDepth = 2 // Maximum nesting level

    return (
      <motion.div
        key={comment.id}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 10 }}
        transition={{ duration: 0.2 }}
        className={depth > 0 ? 'ml-8 border-l-2 pl-4 border-gray-200 dark:border-dark-border' : ''}
      >
        <CommentBubble
          comment={comment}
          workspaceId={workspaceId}
          isOwnComment={isOwnComment}
          onReply={handleReply}
          onEdit={handleEdit}
        />

        {/* Reply input */}
        {isReplying && (
          <div className="mt-4 ml-8">
            <CommentInput
              workspaceId={workspaceId}
              parentCommentId={comment.id}
              placeholder="Write a reply..."
              onCommentCreated={() => {
                handleCommentCreated()
                handleCancelReply()
              }}
              onCancel={handleCancelReply}
              autoFocus
            />
          </div>
        )}

        {/* Nested replies */}
        {hasReplies && depth < maxDepth && (
          <div className="mt-4 space-y-4">
            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </div>
        )}

        {/* Show "View thread" link if max depth reached and more replies exist */}
        {hasReplies && depth >= maxDepth && (
          <div className="mt-2 ml-8">
            <button
              className="text-sm font-medium transition-opacity opacity-70 hover:opacity-100"
              style={{ color: colors.blue[600] }}
              onClick={() => {
                // TODO: Open modal or expand to show full thread
                console.log('View full thread for comment:', comment.id)
              }}
            >
              View {comment.reply_count} more {comment.reply_count === 1 ? 'reply' : 'replies'}
            </button>
          </div>
        )}
      </motion.div>
    )
  }

  if (isLoadingComments) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-gray-400 dark:text-gray-500" size={24} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Top-level comment input */}
      <div>
        <CommentInput
          workspaceId={workspaceId}
          placeholder={t('board:comments.add_placeholder')}
          onCommentCreated={handleCommentCreated}
        />
      </div>

      {/* Comment list */}
      <div className="space-y-6">
        <AnimatePresence>
          {workspaceComments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-gray-500 dark:text-dark-text-muted"
            >
              <p className="text-sm">{t('board:comments.no_comments')}</p>
            </motion.div>
          ) : (
            workspaceComments.map((comment) => renderComment(comment))
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default CommentThread
